/**
 * GFG-364 cycle 2 — the payment step (spec B4, B5, B8; contract-c2 §3.2): its lines and recap, and
 * the postpaid deposit-mode choice. A short wallet is funded in the Buy Credits modal: nothing in
 * the additions flow charges a card.
 *
 * Every figure is the server's; this module only compares and formats them (`formatUsd`).
 * `addRecipients-helper.ts` re-exports the payment-review rules with the rest of the wizard's.
 */

import { COPY } from "@/constants/campaignContacts";
import type {
  BudgetAdditionsQuoteWire,
  RecipientAdditionsQuote,
} from "@/interfaces/campaignContacts";
import { formatUsd, formatUsdChange } from "@/utils/number-format";
import type {
  AdditionCreditsCopy,
  DepositModeOption,
  PaymentLine,
  PaymentLineLabelOverrides,
} from "./types";

/** `campaign_type` of a postpaid campaign (0 prepaid, 1 automated, 2 postpaid). */
const POSTPAID_CAMPAIGN_TYPE = 2;
/** U+2212 MINUS SIGN — a wallet debit reads "−$50.00", as in the prototype. */
const MINUS_SIGN = "−";
/** The recap's verb: what happens to the budget move when the user confirms. */
const RECAP_VERB = { credits: "applied", invoice: "invoiced" } as const;

/**
 * The credits still to buy as the whole number every shortfall message shows (`creditsToBuy` is
 * already rounded up to the Buy Credits drawer's multiple of 5); 1 credit = $1.
 */
export function wholeCredits(creditsToBuy: string): { credits: string; usd: string } {
  const n = Math.ceil(Number(creditsToBuy));
  return { credits: n.toLocaleString("en-US"), usd: formatUsd(n) };
}

/** Postpaid with a short wallet: card (the default) or invoice. Every other quote has no choice. */
export function depositModeOptions(q: RecipientAdditionsQuote): DepositModeOption[] {
  if (q.depositMode !== "card" && q.depositMode !== "invoice") return [];
  const modes = COPY.addWizard.depositModes;
  return [
    { mode: "card", label: modes.card, sub: modes.cardSub },
    { mode: "invoice", label: modes.invoice, sub: modes.invoiceSub },
  ];
}

// ---- payment review (B4, B5, R14, R15) ----------------------------------------------------------

function beforeAfter(figure: { before: string; after: string }): string {
  return formatUsdChange(figure.before, figure.after);
}

/** Postpaid only: the deposit held at the agreed percent, and the top-up it needs. */
function depositLines(q: RecipientAdditionsQuote, budget: BudgetAdditionsQuoteWire): PaymentLine[] {
  if (budget.campaignType !== POSTPAID_CAMPAIGN_TYPE) return [];
  const lines = COPY.addWizard.lines;
  const held: PaymentLine[] =
    budget.deposit !== null && q.depositPercent !== null
      ? [
          {
            key: "depositHeld",
            label: lines.depositHeld(q.depositPercent),
            value: beforeAfter(budget.deposit),
          },
        ]
      : [];
  return [
    ...held,
    { key: "depositTopUp", label: lines.depositTopUp, value: formatUsd(budget.claimAdded) },
  ];
}

/**
 * Where the money comes from: the wallet's credits when it gives any, then either an invoice
 * (postpaid invoice mode) or, when the wallet is still short, the credits still to buy.
 */
function fundingLines(q: RecipientAdditionsQuote): PaymentLine[] {
  const lines = COPY.addWizard.lines;
  const debit = `${MINUS_SIGN}${formatUsd(q.walletDebit)}`;
  const credit: PaymentLine = { key: "walletCredits", label: lines.walletCredits, value: debit };
  const credits = Number(q.walletDebit) > 0 ? [credit] : [];
  if (q.depositMode === "invoice") {
    return [...credits, { key: "invoiced", label: lines.invoiced, value: formatUsd(q.invoiced) }];
  }
  if (Number(q.creditsToBuy) <= 0) return credits;
  const { credits: toBuy, usd } = wholeCredits(q.creditsToBuy);
  const needed = COPY.addWizard.credits.needed(toBuy, usd);
  return [...credits, { key: "creditsNeeded", label: lines.creditsNeeded, value: needed }];
}

/** The payment step's lines, from the quote; `labels` rewords lines by key, never their amounts. */
export function paymentLines(
  q: RecipientAdditionsQuote,
  labels: PaymentLineLabelOverrides = {},
): PaymentLine[] {
  return baseLines(q).map((line) => ({ ...line, label: labels[line.key] ?? line.label }));
}

function baseLines(q: RecipientAdditionsQuote): PaymentLine[] {
  const lines = COPY.addWizard.lines;
  const free: PaymentLine[] =
    q.freeCount > 0
      ? [{ key: "freeSpaces", label: lines.freeSpaces, value: q.freeCount.toLocaleString() }]
      : [];
  const budget = q.budget;
  if (budget === null || q.paidCount === 0) return free;
  const newGifts = `${q.paidCount.toLocaleString()} × ${formatUsd(q.pricePerGift)} = ${formatUsd(
    q.addedValue,
  )}`;
  return [
    ...free,
    { key: "newGifts", label: lines.newGifts, value: newGifts },
    { key: "budget", label: lines.budget, value: beforeAfter(budget.approvedBudget) },
    ...depositLines(q, budget),
    ...fundingLines(q),
  ];
}

/**
 * CONTINUE needs a quote, no refusal (credit limit or credits that would expire first), no request
 * in flight and nothing left to buy: a purchase in the Buy Credits modal shows up as a re-quote
 * with `creditsToBuy` "0".
 */
export function canContinuePayment(q: RecipientAdditionsQuote | undefined, busy: boolean): boolean {
  return q !== undefined && q.refusal === null && !busy && Number(q.creditsToBuy) <= 0;
}

/** The stop-early message when a postpaid addition would exceed the organisation's credit limit. */
export function creditLimitNotice(
  q: RecipientAdditionsQuote,
  copy?: AdditionCreditsCopy,
): string | null {
  const check = q.budget?.creditLimit;
  if (!check || check.ok) return null;
  const headroom = Math.max(0, Number(check.limit) - Number(check.exposure));
  return (copy?.creditLimit ?? COPY.addWizard.creditLimit)(
    formatUsd(check.requested),
    formatUsd(check.limit),
    formatUsd(headroom),
  );
}

/** The recap's headline and verb: invoiced, or the wallet's credits. */
function recapHeadline(q: RecipientAdditionsQuote): { headline: string; verb: string } {
  const recap = COPY.addWizard.recap;
  if (q.depositMode === "invoice") {
    return { headline: recap.invoiced(formatUsd(q.invoiced)), verb: RECAP_VERB.invoice };
  }
  return { headline: recap.credits(formatUsd(q.walletDebit)), verb: RECAP_VERB.credits };
}

/** The send-time step's one-line recap of the payment chosen on the payment step. */
export function paymentRecap(q: RecipientAdditionsQuote): { headline: string; detail: string } {
  const recap = COPY.addWizard.recap;
  const budget = q.budget;
  if (budget === null || q.paidCount === 0) return { headline: recap.free, detail: "" };
  const { headline, verb } = recapHeadline(q);
  const { before, after } = budget.approvedBudget;
  return { headline, detail: recap.detail(formatUsd(before), formatUsd(after), verb) };
}
